import { guest } from '@croissants/contracts';
import type { Locale } from '@croissants/i18n';
import { isResolving, useStalled } from '@croissants/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { seedFirstScreen } from '../hooks/queries';
import {
  adoptSessionCredential,
  api,
  clearSessionCredential,
  isRetriable,
  onSessionRenewed,
  requestFailureKind,
  sessionCredentialGeneration,
  setSessionToken,
} from '../lib/api';
import { readPersistedLocale } from '../lib/locale';
import { qrTokenFromLocation, scrubQrFromAddressBar } from '../lib/qr-url';
import { type SessionScreen, sessionScreen } from '../lib/session-screen';
import { readString, removeKey, writeString } from '../lib/storage';
import { useLocale } from './locale';

/**
 * The QR session (PRD GST-001, GST-028, GST-040).
 *
 * There is no account and no identity provider: the guest is authorized purely by
 * the signed token printed on the tent card in their apartment (tech-stack
 * section 7). The QR token and guest identity survive browser restarts. A renewed
 * bearer is kept only for the current browser session, and the QR is resolved on
 * a later boot, so an expired or revoked code produces
 * the defined screen rather than a wall of failed requests, and a revoked code
 * stops working the moment it is revoked (GST-001, OPS-020).
 *
 * The token is also accepted from the URL in either shape a printed code might
 * carry - a path segment or a query parameter - and is removed from the address
 * bar immediately afterwards: it is a bearer credential and has no business
 * sitting in a shared screenshot or a browser history entry (GST-024, NFR-009).
 *
 * The GUEST KEY is the second thing persisted, and it is what makes "your
 * orders" mean this guest's orders rather than this apartment's. The printed
 * code is screwed to a wall and outlives every stay, so it says where an order
 * came from, never who placed it: scoped by the code alone, the next guest in
 * the apartment reads the previous guest's name, mobile number, access notes
 * and receipt, and can cancel their order too (PRD GST-034, NFR-008, NFR-009).
 * The key is minted by the server on the first scan and replayed on every later
 * one, so a guest keeps their own history across session expiry - and a browser
 * with no key is simply a new guest, which is the correct answer for a phone
 * that has never scanned this code and for one whose storage was cleared.
 */

const QR_STORAGE_KEY = 'cib.qr';
const GUEST_KEY_STORAGE_KEY = 'cib.guest';

export interface SessionContextValue {
  readonly qrToken: string | null;
  readonly session: guest.GuestSession | null;
  /** The single screen this session state opens onto; see `lib/session-screen`. */
  readonly screen: SessionScreen;
  readonly adoptToken: (token: string) => void;
  /** Drops the code entirely - stored, in-memory and in the address bar. */
  readonly forget: () => void;
  /**
   * Re-runs the resolve, for the screens that offer the guest a retry. Settles
   * when the resolve has, so a caller that must act on the NEW session - mode
   * B2's re-scan onto the next closest shop - can wait for it.
   */
  readonly retry: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const { locale, adoptLocale } = useLocale();
  const queryClient = useQueryClient();

  const urlToken = useMemo(
    () => qrTokenFromLocation(globalThis.location?.href ?? 'https://localhost/'),
    [],
  );

  /**
   * The code in play, as state rather than as a value recomputed from the URL and
   * storage on every render.
   *
   * A token in the URL is authoritative and replaces whatever was stored, so a
   * second apartment's code scanned on the same phone opens that apartment. But
   * it must also be *droppable*: while this was derived from a `useMemo` snapshot
   * of the URL, `forget()` could clear localStorage and still leave the dead code
   * in play, because nothing could reset the snapshot. The rescan button on the
   * invalid-code screen - the guest's only way out - then did nothing at all on
   * the one journey where it matters, the first scan of a revoked card.
   */
  const [qrToken, setQrToken] = useState<string | null>(
    () => urlToken ?? readString(QR_STORAGE_KEY),
  );

  useEffect(() => {
    if (urlToken === null) return;
    /**
     * A token in the URL is a scan that JUST happened, and it REPLACES any
     * session this phone was carrying - loudly, not by accident. When the
     * stored code is a DIFFERENT apartment's, everything cached under it
     * (storefront, orders, windows) belongs to that apartment and is dropped
     * outright, so nothing of the previous flat can render for even a frame
     * under the new one (TST-008: a reused phone was silently served the
     * previous apartment's storefront, and the order followed the wrong QR).
     */
    const stored = readString(QR_STORAGE_KEY);
    if (stored !== null && stored !== urlToken) {
      clearSessionCredential();
      queryClient.clear();
    }
    writeString(QR_STORAGE_KEY, urlToken);
    scrubQrFromAddressBar();
  }, [urlToken, queryClient]);

  const query = useQuery({
    queryKey: ['session', qrToken],
    enabled: qrToken !== null,
    // A session token is short-lived; re-resolving on reconnect keeps a guest who
    // walked into a lift from meeting an authorization error on their next tap.
    staleTime: 60_000,
    /**
     * Only a failure that could answer differently is worth repeating.
     *
     * A 404 on a forged code was asked twice and refused twice, which bought
     * nothing but a second entry in the worker log and a longer window in which
     * the query was neither settled nor running - and that window is exactly
     * where the guest was shown "we could not reach the server" about a code the
     * server had already refused. A definitive refusal now settles on the first
     * response; a dropped connection still gets three attempts.
     */
    retry: (failureCount, error) => isRetriable(error) && failureCount < 2,
    queryFn: async () => {
      if (qrToken === null) return null;
      const generation = sessionCredentialGeneration();
      const guestKey = readString(GUEST_KEY_STORAGE_KEY);
      const { firstScreen, ...resolved } = await api.call(guest.resolveSession, {
        body: {
          qrToken,
          preferredLocale: locale,
          ...(guestKey === null ? {} : { guestKey }),
        },
      });
      // Kept before the storefront renders, so the first order placed on a
      // fresh phone is already attributable to the identity that placed it.
      // A resolve that opened no apartment issues no identity, and there is
      // nothing to keep - storing its placeholder would make the next real
      // scan present a key the server will refuse.
      if (resolved.servable && !resolved.servicePaused) {
        writeString(GUEST_KEY_STORAGE_KEY, resolved.guestKey);
      }
      adoptSessionCredential(resolved.sessionToken, resolved.expiresAt, generation);
      // The first screen's reads came back with the scan: filed before the
      // session renders, so that screen opens on them (`seedFirstScreen`) -
      // and only while this is still the code in play, so a second code
      // scanned meanwhile never opens on the first apartment's shelf.
      if (firstScreen !== undefined && sessionCredentialGeneration() === generation) {
        seedFirstScreen(queryClient, firstScreen);
      }
      return resolved;
    },
  });

  const session = query.data ?? null;

  /**
   * Every later request carries the session token, held in the client module
   * rather than threaded through each call site.
   *
   * Assigned during render, deliberately, not in an effect. React flushes child
   * effects before parent ones, so a screen's first query - the storefront, the
   * quote - subscribes and fetches before a `useEffect` here would have run, and
   * that request goes out with no session token and comes back 401. The
   * assignment is idempotent and derived purely from the rendered value, so doing
   * it here is safe and is the only ordering that has the token in place before
   * the first child request leaves.
   */
  setSessionToken(session?.sessionToken ?? null);

  useEffect(
    () =>
      onSessionRenewed(({ token, expiresAt }) => {
        queryClient.setQueryData<guest.GuestSession>(['session', qrToken], (previous) =>
          previous === undefined ? undefined : { ...previous, sessionToken: token, expiresAt },
        );
      }),
    [queryClient, qrToken],
  );

  /**
   * A locale the guest chose on this device outranks the session's - it is the
   * more recent statement of intent, and `resolveSession` is handed it so the
   * session adopts it rather than the other way round. Only a guest who has never
   * touched the switch inherits the session's language (PRD GST-006).
   */
  const hasOwnPreference = useMemo(() => readPersistedLocale() !== null, []);
  const sessionLocale = session?.locale;
  useEffect(() => {
    if (hasOwnPreference) return;
    // Adopted, not set: writing it back would patch the session with the value it
    // has just told us it holds.
    if (sessionLocale !== undefined && sessionLocale !== locale) adoptLocale(sessionLocale);
  }, [hasOwnPreference, sessionLocale, locale, adoptLocale]);

  const adoptToken = useCallback(
    (token: string) => {
      if (token !== qrToken) clearSessionCredential();
      writeString(QR_STORAGE_KEY, token);
      setQrToken(token);
      void queryClient.invalidateQueries({ queryKey: ['session'] });
    },
    [queryClient, qrToken],
  );

  /**
   * Drops the code from storage, from state and from the address bar, so a
   * reload cannot resurrect it. `queryClient.clear()` goes last: everything
   * cached - storefront, orders, basket scope - belonged to that apartment.
   *
   * The guest key deliberately stays. This drops an APARTMENT, not a person -
   * it is the way out of a revoked or mistyped code - and the guest who scans
   * the right one next is still the same guest, with the same orders behind
   * them (PRD GST-034).
   */
  const forget = useCallback(() => {
    removeKey(QR_STORAGE_KEY);
    clearSessionCredential();
    setQrToken(null);
    scrubQrFromAddressBar();
    queryClient.clear();
  }, [queryClient]);

  const refetch = query.refetch;
  const retry = useCallback(async (): Promise<void> => {
    await refetch();
  }, [refetch]);

  /**
   * The backstop behind the resolving spinner, on the platform's ONE deadline
   * (`RESOLVE_PATIENCE_MS` in `@croissants/ui`).
   *
   * Nothing is cancelled - a slow resolve that lands after this still opens the
   * storefront on its own. It exists so that a fetch which is marked in flight
   * but will never settle ends on a screen the guest can act on rather than on a
   * spinner nobody is coming back to. The guest used to wait 15 s here against
   * the 12 s every other portal waits, off a second constant and a second copy
   * of this timer: the phone, of all four readers, waited longest.
   */
  const resolveStalled = useStalled(isResolving(query));

  /**
   * What the last attempt actually hit. `failureReason` carries the error of the
   * attempt that just failed while the query is still retrying, and `error`
   * carries it once the query settles - so the screen knows the code was refused
   * from the first refusal onwards, not only after the retries run out.
   */
  const failure = requestFailureKind(query.error ?? query.failureReason);

  const screen = sessionScreen({
    qrToken,
    status: query.status,
    fetchStatus: query.fetchStatus,
    session,
    failure,
    resolveStalled,
  });

  const value = useMemo<SessionContextValue>(
    () => ({ qrToken, session, screen, adoptToken, forget, retry }),
    [qrToken, session, screen, adoptToken, forget, retry],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === null) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

/**
 * Pushes the chosen language onto the QR session so notifications follow it
 * (PRD GST-006, NTF-004). Failure is deliberately silent: the storefront is
 * already in the new language, and a guest must never see an error for changing
 * a language switch.
 *
 * The callback has a STABLE identity, and that is not tidiness. It is
 * `<LocaleProvider onChange={...}>`'s prop (`main.tsx`), and the shared context
 * carries `onChange` into `setLocale`'s dependencies, `setLocale` into the
 * context value's, and the value into the effect that sets `<html lang>` and the
 * `accept-language` header. A new arrow per render therefore rebuilt the context
 * value on every render of `Providers` - which `useMutation` re-renders on every
 * mutation state change anywhere in the app - and re-ran that effect and every
 * consumer of `useLocale()` with it, on a phone, for a language nobody had
 * touched.
 */
export function useLocalePersistence(): (locale: Locale) => void {
  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: async ({
      locale,
      qrToken,
      generation,
    }: {
      locale: Locale;
      qrToken: string;
      generation: number;
    }) => ({
      reissued: await api.call(guest.setLocale, { body: { locale } }),
      qrToken,
      generation,
    }),
    /*
     * MERGED onto the session already held, not swapped for it. The endpoint
     * answers with the reissued token and the two facts reissuing changed
     * (`GuestSessionReissue`); the apartment, the mode, the welcome message and
     * the cutoff are unchanged by definition, so asking the server to rebuild
     * and resend them cost 17 database round trips to move a language
     * (docs/portal-latency-audit.html).
     */
    onSuccess: ({ reissued, qrToken, generation }) => {
      // The language request belongs to the QR that started it. A second QR
      // scanned while it was in flight must not let the old apartment replace
      // the new session merely because this response has an equal or later
      // expiry.
      if (readString(QR_STORAGE_KEY) !== qrToken || sessionCredentialGeneration() !== generation) {
        return;
      }
      queryClient.setQueryData<guest.GuestSession>(['session', qrToken], (previous) =>
        previous === undefined ? undefined : { ...previous, ...reissued },
      );
      adoptSessionCredential(reissued.sessionToken, reissued.expiresAt, generation);
      /*
       * And NOTHING is refetched. The token's locale claim still decides what
       * a notification is written in (NTF-004), which is why this call is made
       * at all - but no screen waits for it any more. Every server-authored
       * string now travels in all shipped languages beside the served one
       * (NFR-006) and the browser picks with `textIn`, so the switch is one
       * render. It used to reissue the token and re-ask the storefront, the
       * order list and the order detail, and for the second that took, half a
       * screen was in the new language and half in the old.
       */
    },
  });
  return useCallback(
    (locale: Locale) => {
      const qrToken = readString(QR_STORAGE_KEY);
      if (qrToken === null) return;
      mutate({ locale, qrToken, generation: sessionCredentialGeneration() });
    },
    [mutate],
  );
}
